import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import ForumIcon from "@mui/icons-material/Forum";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PeopleIcon from "@mui/icons-material/People";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  collection,
  query,
  where,
  onSnapshot,
  limit,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { FormLabel } from "../../components/form-label/FormLabel";
import { tenantService } from "../../services/tenantService";
import { Conversation, CollectedData, Tenant } from "../../types";

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  loading: boolean;
  onClick?: () => void;
}

const MetricCard = ({ icon, label, value, loading, onClick }: MetricCardProps) => (
  <Card
    variant="outlined"
    sx={{ flex: 1, cursor: onClick ? "pointer" : "default" }}
    onClick={onClick}
  >
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ color: "primary.main", fontSize: 40 }}>{icon}</Box>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          {loading ? <CircularProgress size={20} /> : value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

export default function MonitoringDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantIdParam = searchParams.get("tenantId") ?? "";

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantsError, setTenantsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [convCount, setConvCount] = useState<number>(0);
  const [collectedCount, setCollectedCount] = useState<number>(0);
  const [activeConvCount, setActiveConvCount] = useState<number>(0);
  const [loadingConv, setLoadingConv] = useState(false);
  const [loadingCollected, setLoadingCollected] = useState(false);

  useEffect(() => {
    tenantService
      .list()
      .then((res) => setTenants(res.data.data.tenants))
      .catch((e) => {
        console.error(e);
        setTenantsError("Errore nel caricamento dei tenant");
      });
  }, []);

  // Conversations snapshot
  useEffect(() => {
    if (!tenantIdParam) {
      setConvCount(0);
      setActiveConvCount(0);
      return;
    }
    setLoadingConv(true);
    const q = query(
      collection(db, "conversations"),
      where("tenant_id", "==", tenantIdParam),
      limit(200)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs.map((d) => d.data() as Conversation);
        setConvCount(docs.length);
        setActiveConvCount(
          docs.filter((c) => c.flow_state?.status === "active").length
        );
        setLoadingConv(false);
      },
      (err) => {
        console.error(err);
        setError("Errore nel caricamento delle conversazioni");
        setLoadingConv(false);
      }
    );
    return () => unsub();
  }, [tenantIdParam]);

  // Collected data snapshot
  useEffect(() => {
    if (!tenantIdParam) {
      setCollectedCount(0);
      return;
    }
    setLoadingCollected(true);
    const q = query(
      collection(db, "collected_data"),
      where("tenant_id", "==", tenantIdParam),
      limit(200)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setCollectedCount(snap.docs.length);
        setLoadingCollected(false);
      },
      (err) => {
        console.error(err);
        setError("Errore nel caricamento dei dati raccolti");
        setLoadingCollected(false);
      }
    );
    return () => unsub();
  }, [tenantIdParam]);

  const convUrl = tenantIdParam
    ? `/chatbot/monitoring/conversations?tenantId=${tenantIdParam}`
    : "/chatbot/monitoring/conversations";
  const collectedUrl = tenantIdParam
    ? `/chatbot/monitoring/collected?tenantId=${tenantIdParam}`
    : "/chatbot/monitoring/collected";

  return (
    <div className="container">
      <FormLabel label="Monitoring — Dashboard" />

      {tenantsError && <Alert severity="error" sx={{ mb: 2 }}>{tenantsError}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ mb: 3, maxWidth: 320 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="tenant-select">Tenant</InputLabel>
          <Select
            labelId="tenant-select"
            label="Tenant"
            value={tenantIdParam}
            onChange={(e) =>
              setSearchParams(e.target.value ? { tenantId: e.target.value } : {})
            }
          >
            <MenuItem value="">— seleziona tenant —</MenuItem>
            {tenants.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {!tenantIdParam ? (
        <Alert severity="info">Seleziona un tenant per visualizzare le metriche.</Alert>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <MetricCard
              icon={<ForumIcon fontSize="inherit" />}
              label="Conversazioni totali"
              value={convCount}
              loading={loadingConv}
              onClick={() => navigate(convUrl)}
            />
            <MetricCard
              icon={<PeopleIcon fontSize="inherit" />}
              label="Flow attivi ora"
              value={activeConvCount}
              loading={loadingConv}
              onClick={() => navigate(convUrl)}
            />
            <MetricCard
              icon={<AssignmentIcon fontSize="inherit" />}
              label="Dati raccolti (flow completati)"
              value={collectedCount}
              loading={loadingCollected}
              onClick={() => navigate(collectedUrl)}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button variant="outlined" onClick={() => navigate(convUrl)}>
              Vedi conversazioni
            </Button>
            <Button variant="outlined" onClick={() => navigate(collectedUrl)}>
              Vedi dati raccolti
            </Button>
          </Box>
        </>
      )}
    </div>
  );
}
